import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { useParallax } from '@/hooks/use-parallax'

interface FoliageProps {
    side: 'left' | 'right'
    className?: string
    preload?: boolean
    parallax?: number
}

const SOURCES = {
    left: '/images/foliage-left.png',
    right: '/images/foliage-right.png'
}

/*
 * Painted branches on a white ground. multiply makes the white disappear into
 * whatever sits behind, so keep every ancestor free of transform, opacity,
 * filter or isolation, or the white box shows. Motion lives on the blended
 * element itself (parallax) and on the image inside it (sway), which is safe.
 */
export const Foliage: FC<FoliageProps> = ({ side, className, preload = false, parallax = 0.12 }): ReactNode => {
    const ref = useParallax<HTMLDivElement>(parallax)

    return (
        <div
            ref={ref}
            aria-hidden='true'
            className={cn('pointer-events-none absolute z-10 hidden mix-blend-multiply select-none will-change-[translate] [mask-composite:intersect] [mask-image:linear-gradient(to_bottom,transparent,#000_12%,#000_88%,transparent),linear-gradient(to_right,transparent,#000_6%,#000_80%,transparent)] lg:block', side === 'right' && '[mask-image:linear-gradient(to_bottom,transparent,#000_12%,#000_88%,transparent),linear-gradient(to_left,transparent,#000_6%,#000_80%,transparent)]', className)}
        >
            <Image
                src={SOURCES[side]}
                alt=''
                width={1536}
                height={2752}
                sizes='(min-width: 1024px) 36vw, 1px'
                preload={preload}
                className={cn('h-auto w-full', side === 'left' ? 'foliage-sway' : 'foliage-sway-right')}
            />
        </div>
    )
}
